import { useCallback, useRef, useEffect, useState } from "react";

const CART_STORAGE_KEY = "pdv_cart_";
const cartStore = new Map();

const useCartPersistence = (ticketId) => {
  const previousTicketId = useRef(null);
  const isInitialMount = useRef(true);
  const [cart, setCartState] = useState([]);

  const getStorageKey = useCallback((id) => `${CART_STORAGE_KEY}${id}`, []);

  const persistCart = useCallback((items) => {
    if (!ticketId) return;
    const stateToSave = { items: items || [], savedAt: Date.now() };
    cartStore.set(ticketId, stateToSave);
    try {
      localStorage.setItem(getStorageKey(ticketId), JSON.stringify(stateToSave));
    } catch (err) { console.warn("Erro ao salvar carrinho:", err); }
  }, [ticketId, getStorageKey]);

  const saveCart = useCallback((cartState) => {
    if (!ticketId) return;
    const stateToSave = { ...cartState, savedAt: Date.now() };
    cartStore.set(ticketId, stateToSave);
    try {
      localStorage.setItem(getStorageKey(ticketId), JSON.stringify(stateToSave));
    } catch (err) { console.warn("Erro ao salvar carrinho:", err); }
  }, [ticketId, getStorageKey]);

  const loadCart = useCallback((targetTicketId) => {
    const id = targetTicketId || ticketId;
    if (!id) return null;
    if (cartStore.has(id)) return cartStore.get(id);
    try {
      const saved = localStorage.getItem(getStorageKey(id));
      if (saved) { const parsed = JSON.parse(saved); cartStore.set(id, parsed); return parsed; }
    } catch (err) { console.warn("Erro ao carregar carrinho:", err); }
    return null;
  }, [ticketId, getStorageKey]);

  const clearCart = useCallback((targetTicketId) => {
    const id = targetTicketId || ticketId;
    if (!id) return;
    cartStore.delete(id);
    setCartState([]);
    try { localStorage.removeItem(getStorageKey(id)); } catch (err) {}
  }, [ticketId, getStorageKey]);

  const setCart = useCallback((newCart) => {
    const items = typeof newCart === "function" ? newCart(cart) : newCart;
    setCartState(items);
    persistCart(items);
  }, [cart, persistCart]);

  const addToCart = useCallback((product) => {
    const qtyToAdd = product.quantity || 1;
    setCartState(prevCart => {
      const idx = prevCart.findIndex(item => item.id === product.id);
      let newCart;
      if (idx >= 0) {
        newCart = prevCart.map((item, i) => {
          if (i === idx) {
            const newQty = (item.quantity || 1) + qtyToAdd;
            const unitPrice = item.unitPrice || item.price || 0;
            return { ...item, quantity: newQty, total: newQty * unitPrice };
          }
          return item;
        });
      } else {
        const price = product.unitPrice || product.price || 0;
        const originalPrice = product.originalPrice || price;
        newCart = [...prevCart, {
          ...product,
          quantity: qtyToAdd,
          originalPrice: originalPrice,
          unitPrice: price,
          total: price * qtyToAdd,
          discountPercent: product.discountPercent || 0,
          discount: product.discount || 0
        }];
      }
      persistCart(newCart);
      return newCart;
    });
  }, [persistCart]);

  const updateQuantity = useCallback((productId, quantity) => {
    setCartState(prevCart => {
      const newCart = prevCart.map(item => {
        if (item.id === productId) {
          const newQty = Math.max(1, quantity);
          const unitPrice = item.unitPrice || item.price || 0;
          return { ...item, quantity: newQty, total: newQty * unitPrice };
        }
        return item;
      });
      persistCart(newCart);
      return newCart;
    });
  }, [persistCart]);

  const removeItem = useCallback((productId) => {
    setCartState(prevCart => {
      const newCart = prevCart.filter(item => item.id !== productId);
      persistCart(newCart);
      return newCart;
    });
  }, [persistCart]);

  const applyDiscount = useCallback((productId, discountPercent) => {
    setCartState(prevCart => {
      const newCart = prevCart.map(item => {
        if (item.id === productId) {
          const discountPct = Math.min(100, Math.max(0, discountPercent || 0));
          const originalPrice = item.originalPrice || item.price || 0;
          const discountValue = originalPrice * (discountPct / 100);
          const unitPrice = originalPrice - discountValue;
          return {
            ...item,
            discountPercent: discountPct,
            discount: discountValue,
            unitPrice: unitPrice,
            total: (item.quantity || 1) * unitPrice
          };
        }
        return item;
      });
      persistCart(newCart);
      return newCart;
    });
  }, [persistCart]);

  const hasCart = useCallback((targetTicketId) => {
    const id = targetTicketId || ticketId;
    if (!id) return false;
    if (cartStore.has(id)) return (cartStore.get(id)?.items?.length || 0) > 0;
    try {
      const saved = localStorage.getItem(getStorageKey(id));
      if (saved) return (JSON.parse(saved)?.items?.length || 0) > 0;
    } catch (err) {}
    return false;
  }, [ticketId, getStorageKey]);

  const getCartItemsCount = useCallback((targetTicketId) => {
    const cartData = loadCart(targetTicketId || ticketId);
    return cartData?.items?.length || 0;
  }, [loadCart, ticketId]);

  const getAllActiveCarts = useCallback(() => {
    const activeCarts = [];
    cartStore.forEach((cartData, id) => {
      if (cartData?.items?.length > 0) {
        activeCarts.push({ ticketId: id, itemsCount: cartData.items.length, total: cartData.items.reduce((s, i) => s + (i.total || 0), 0), savedAt: cartData.savedAt, customerName: cartData.customer?.name });
      }
    });
    return activeCarts.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  }, []);

  useEffect(() => {
    if (ticketId) {
      const savedCart = loadCart(ticketId);
      setCartState(savedCart?.items || []);
    } else { setCartState([]); }
  }, [ticketId, loadCart]);

  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; previousTicketId.current = ticketId; return; }
    if (previousTicketId.current !== ticketId) previousTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    const maxAge = 24 * 60 * 60 * 1000;
    const now = Date.now();
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CART_STORAGE_KEY)) {
          const saved = localStorage.getItem(key);
          if (saved) { const d = JSON.parse(saved); if (d.savedAt && (now - d.savedAt) > maxAge) keysToRemove.push(key); }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (err) {}
  }, []);

  return { cart, setCart, addToCart, updateQuantity, removeItem, applyDiscount, clearCart, saveCart, loadCart, hasCart, getCartItemsCount, getAllActiveCarts, previousTicketId: previousTicketId.current };
};

export default useCartPersistence;
